/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Form, Icon, Input, Checkbox, Button, Alert } from 'antd';
import { useHistory } from 'react-router-dom';
import { ReactComponent as TwoFactorIcon } from '../assets/twoFactorIcon.svg';
import {
  OTP_TYPE,
  isAuthenticatorOtpType,
  isSmsSecondFactorOptionAvailable,
  getAuthenticatorAppLabel,
} from '../../../constants/mfa';
import { MFAFormVerifyStep } from './MFAFormVerifyStep';
import resolvePostLoginPath from '../resolvePostLoginPath';

const FormItem = Form.Item;

const Intro = () => (
  <div className="intro">
    <h3>
      <Icon component={TwoFactorIcon} className="two-factor-icon" /> Introducing Two - Factor Authentication (2FA)
    </h3>
    <div className="description">
      <Icon type="exclamation-circle" className="exclamation-icon" />
      <ul>
        <li>
          For added security, Two-Factor Authentication (2FA) introduces an additional layer of protection to your
          account. In addition to your primary email and password, you will be required to input a code sent via email
          {isSmsSecondFactorOptionAvailable()
            ? ' or SMS, Microsoft Authenticator, or Google Authenticator.'
            : ' or Microsoft Authenticator or Google Authenticator.'}
        </li>
        <li>You can configure MFA at any time in the future by navigating to Admin &gt; Settings.</li>
      </ul>
    </div>
  </div>
);

function shouldHardNavigateAfterLogin(pathname = window.location.pathname) {
  return (
    pathname.startsWith('/reset-password') ||
    pathname.startsWith('/invitation/') ||
    pathname.startsWith('/authenticator-recovery')
  );
}

const MFAForm = ({
  form,
  fetchBroker,
  fetchOrganisations,
  loginRequest,
  loginHasError,
  sendOtp,
  verifyOtp,
  needResend,
  sendConfirmationRequest,
  sendConfirmationEmail,
  setSendConfirmationRequest,
  setErrorMessage,
  errorMessage,
  setLoginStatus,
  close,
}) => {
  const [codeSent, setCodeSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState();
  const [userInfo, setUserInfo] = useState();
  const history = useHistory();
  const { getFieldDecorator } = form;

  // prevent state updates after unmount when async calls resolve
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const codeInput = useCallback((inputElement) => {
    if (inputElement) {
      inputElement.focus();
    }
  }, []);

  const navigateAfterLogin = async () => {
    const redirectPath = await resolvePostLoginPath({ fetchBroker, fetchOrganisations });
    if (typeof close === 'function') close();
    // Reset-password / invitation pages must fully leave so the form cannot stay visible after auth.
    if (shouldHardNavigateAfterLogin()) {
      window.location.assign(redirectPath);
      return;
    }
    history.replace(redirectPath);
  };

  const onVerify = (e) => {
    e.preventDefault();
    form.validateFields(async (err, { otpCode }) => {
      if (!err) {
        await verifyOtp({ otpCode: `${otpCode}`, userId: userInfo?.userId, rememberMe });
        if (isMountedRef.current) setCodeSent(false);
        await navigateAfterLogin();
      }
    });
  };

  const onLogin = (e) => {
    e.preventDefault();
    form.validateFields(async (err, { email, password, rememberMe }) => {
      if (!err) {
        if (isMountedRef.current) setEmail(email);
        if (isMountedRef.current) setPassword(password);
        if (isMountedRef.current) setRememberMe(rememberMe);
        const userSetting = await sendOtp({ email, password });
        if (userSetting) {
          if (isMountedRef.current) setUserInfo(userSetting);
        }
        if (isMountedRef.current) setCodeSent(true);
      }
    });
  };

  const onResendOtp = async () => {
    await sendOtp({ email, password });
  };

  const resetLoginStatus = () => {
    setLoginStatus(null);
    setErrorMessage('');
  };

  const onResendConfirmationEmail = async () => {
    await sendConfirmationEmail({ email, password });
    setSendConfirmationRequest(false);
    resetLoginStatus();
  };

  const isTotpLogin = Boolean(codeSent && isAuthenticatorOtpType(userInfo?.otpType));
  const authenticatorLabel = getAuthenticatorAppLabel(userInfo?.otpType);

  return (
    <>
      {loginHasError && errorMessage !== '' && <Alert message={errorMessage} type="error" />}

      {codeSent && !errorMessage && (
        <Alert
          type="success"
          showIcon
          className={isTotpLogin ? 'login-mfa-alert login-mfa-alert--totp' : 'login-mfa-alert'}
          message={isTotpLogin ? `${authenticatorLabel} sign-in` : 'Check your messages'}
          description={
            isTotpLogin
              ? `Open ${authenticatorLabel} and enter the 6-digit code for ContinuityCoach `
                + '(it updates every 30 seconds).'
              : `A one-time passcode has been sent via ${
                  userInfo?.otpType === OTP_TYPE.EMAIL ? 'email' : 'SMS'
                }.`
          }
        />
      )}

      {needResend && (
        <>
          <Alert
            message="Your account has not been activated."
            description={errorMessage}
            type="warning"
            showIcon
            className="login-alert"
          />
          <Button type="default" onClick={resetLoginStatus} style={{ marginLeft: 10 }} className="login-alert-btn">
            Login
          </Button>
          <Button
            type="primary"
            loading={sendConfirmationRequest}
            onClick={onResendConfirmationEmail}
            className="login-alert-btn"
          >
            {sendConfirmationRequest ? 'Sending' : 'Resend'}
          </Button>
        </>
      )}

      {!needResend &&
        (codeSent ? (
          <MFAFormVerifyStep
            isTotpLogin={isTotpLogin}
            authenticatorLabel={authenticatorLabel}
            getFieldDecorator={getFieldDecorator}
            codeInputRef={codeInput}
            loginRequest={loginRequest}
            onSubmit={onVerify}
            onResendOtp={onResendOtp}
          />
        ) : (
          <Form onSubmit={onLogin} className="login-form">
            <Intro />
            <FormItem>
              {getFieldDecorator('email', {
                validateTrigger: 'onBlur',
                rules: [{ required: true, message: 'Username is required' }],
              })(
                <Input
                  prefix={<Icon type="user" style={{ fontSize: 13 }} />}
                  placeholder="Username"
                  disabled={loginRequest}
                  spellCheck={false}
                />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('password', {
                rules: [{ required: true, message: 'Password is required' }],
              })(
                <Input
                  prefix={<Icon type="lock" style={{ fontSize: 13 }} />}
                  type="password"
                  placeholder="Password"
                  disabled={loginRequest}
                />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('rememberMe', {
                valuePropName: 'checked',
                initialValue: false,
              })(<Checkbox>Remember me</Checkbox>)}
              <a href="/reset-password" className="login-link">
                Forgot password
              </a>
              <Button type="primary" htmlType="submit" loading={loginRequest} className="login-form-btn">
                {loginRequest ? 'Logging in' : 'Log in'}
              </Button>
            </FormItem>
          </Form>
        ))}
    </>
  );
};

export default Form.create()(MFAForm);
